/* eslint-disable no-restricted-globals */
import classNames from 'classnames';
import { messageLog } from 'utilities/helpers/logger/message-logger.js';

/**
 * Move element to another dom element // e.g. when map takeover needs to move into different dom object
 * Pass in selector for Class ('.classSelector') or ID ('#idSelector)
 * @typedef {Function} moveAd
 * @param {string} containerSelector Dom Item selector to move
 * @param {string} newParentSelector New Parent selector to move container into
 * @example
 * // moves element with 'containerClass' into element with 'newParentId'
 * adUtils.moveAd('.containerClass', '#newParentId')
 * @returns {void}
 */

/**
 * Add class to element
 * Pass in selector for Class ('.classSelector') or ID ('#idSelector)
 * @typedef {Function} addClass
 * @param {string} elementSelector Element selector to add class to
 * @param {string} className ClassName to add
 * @example
 * // adds class 'superClass' to element with 'containerClass'
 * adUtils.addClass('.containerClass', 'superClass')
 * @returns {void}
 */

/**
 * Hide Iframe of current creative wrapper
 * @typedef {Function} hideAd
 * @example
 * // hides this creative wrappers iframe
 * adUtils.hideAd()
 * @returns {void}
 */

/**
 * The return object from AdUtilsInit
 * @typedef {Object} AdUtils
 * @property {moveAd} moveAd Move dom from one selector to another -- see MoveAd
 * @property {addClass} addClass Add a class to an element -- see addClass
 * @property {hideAd} hideAd Hide iframe -- see hideAd
 */

/**
 * Initialise Ad Unit Helpers with core arguments
 * @param {document} documentRef The creative wrapper document object (we need this to access elements within the iframe dom)
 * @param {string=} debugName Optional debug string, if string is passed then turns on debug mode, if no arg passed then not in debug mode
 * @example
 * // returns AdUtils in debug mode
 * var adUtils = window.parent.AdUtils(document, 'fuse-tag-1234')
 * // returns AdUtils in prod mode
 * var adUtils = window.parent.AdUtils(document)
 * @returns {AdUtils} Ad functions
 */
export function AdUtilsInit(
  documentRef: Document,
  debugName = '',
): {
  moveAd: (containerSelector: string, newParentSelector: string) => void;
  addClass: (elementSelector: string, className: string) => void;
  addAdvertMapIcon: (
    _div: HTMLDivElement,
    _townSelector: string,
    position?: Partial<Record<'right' | 'left' | 'top', number>>,
  ) => void;
  hideAd: () => void;
  info: () => string;
  createToolTipDiv: (addClass: string) => HTMLDivElement;
  createToolTipLink: (addClass: string, link: string) => HTMLAnchorElement;
} {
  const _msgLog = (...d: Parameters<typeof messageLog.ads>) => {
    if (debugName) {
      messageLog.ads(...d);
    }
  };

  const _getSelector = (containerSelector: string): HTMLElement | null => {
    return documentRef?.querySelector(containerSelector) ?? document.querySelector(containerSelector);
  };

  const _getSelectorAll = (containerSelector: string): NodeListOf<Element> => {
    const docRef = documentRef?.querySelectorAll(containerSelector);
    if (docRef?.length > 0) {
      return docRef;
    }
    return document.querySelectorAll(containerSelector);
  };

  /**
   * This is to allow ads to be moved outside of their container e.g. with mapTakeovers
   * @param containerSelector
   * @param newParentSelector
   */
  function moveAd(containerSelector: string, newParentSelector: string) {
    const container = _getSelector(containerSelector);
    const newParent = _getSelector(newParentSelector);
    _msgLog(
      `AdUtils: ${debugName} MoveAd`,
      [`ContainerEl ${containerSelector}`, container],
      [`NewParentEl ${newParentSelector}`, newParent],
    );

    if (container && newParent) {
      const oldParent = container.parentNode;
      if (oldParent) {
        while (oldParent.childNodes.length) {
          const childNode = oldParent.firstChild;
          if (childNode) {
            newParent.appendChild(oldParent.firstChild);
          }
        }
      }
    }
  }

  /**
   *
   * @param elementSelector
   * @param className
   */
  function addClass(elementSelector: string, className: string) {
    const element = _getSelector(elementSelector);
    if (element) {
      element.classList.add(className);
    }
    _msgLog(`AdUtils: ${debugName} AddClass`, [`NewElementSelector ${elementSelector}`, element]);
  }

  function createToolTipDiv(
    classes: string = 'Map-tooltip--position Map-tooltip-content u-inlineBlock u-posAbs Ad-tooltip-content u-hidden u-xl-block',
  ): HTMLDivElement {
    const div = document.createElement('div');
    div.className = classes;
    return div;
  }

  function createToolTipLink(addClass: string, desiredLink: string): HTMLAnchorElement {
    const a = document.createElement('a');
    a.className = classNames(
      'Map-tooltip--position Map-tooltip-content u-inlineBlock u-posAbs Ad-tooltip-content u-hidden u-xl-block',
      addClass,
    );
    a.setAttribute('href', desiredLink);
    a.setAttribute('target', '_blank');
    return a;
  }

  function addAdvertMapIcon(
    _div: HTMLDivElement,
    _townSelector: string,
    position: Partial<Record<'right' | 'left' | 'top', number>> = {},
  ): void {
    /** Create All Element Function */
    const div = _div.cloneNode(true) as HTMLDivElement;
    if (position.right) {
      div.style.right = `${position.right}px`;
    }
    if (position.left) {
      div.style.left = `${position.left}px`;
    }
    if (position.top) {
      div.style.top = `${position.top}px`;
    }
    const townSelector = _townSelector.toLowerCase().replace(' ', '-');
    _getSelectorAll('.Map.Map--primary.Map--static .Map-cartography-image > .Map-tooltip').forEach((df) => {
      const linkEl = df.querySelector('a');
      if (linkEl?.id === `town-${townSelector}`) {
        const existingAdEl = linkEl.querySelector('div.Ad-tooltip-content');
        /** Only add if not already there */
        if (!existingAdEl) {
          linkEl.appendChild(div);
        }
      }
    });
  }

  /**
   *
   */
  function hideAd() {
    //just for reference, top is the top document, self is the current document and parent is the document above the current one.
    const haystack = document.getElementsByTagName('IFRAME') as unknown as Window[];
    const needle = documentRef.defaultView;
    _msgLog(`AdUtils: ${debugName} hideAd`, [`IFrames`, haystack], ['defaultView', needle]);
    for (const frame of haystack) {
      if (frame.name === needle?.name) {
        _msgLog(`AdUtils: ${debugName} hideAd Found ${frame.name}`);
        const val = document.getElementById(frame.name);
        if (val) {
          val.style.display = 'none';
        }
      }
    }
  }

  function info() {
    return `
      Available functions: 
       
      MoveAd
      * Move element to another dom element // e.g. when map takeover needs to move into different dom object
      * Pass in selector for Class ('.classSelector') or ID ('#idSelector)
      * @typedef {Function} moveAd
      * @param {string} containerSelector Dom Item selector to move
      * @param {string} newParentSelector New Parent selector to move container into
      * @example
      * // moves element with 'containerClass' into element with 'newParentId'
      * adUtils.moveAd('.containerClass', '#newParentId')
      * @returns {void}
 
      addClass
      * Add class to element
      * Pass in selector for Class ('.classSelector') or ID ('#idSelector)
      * @typedef {Function} addClass
      * @param {string} elementSelector Element selector to add class to
      * @param {string} className ClassName to add
      * @example
      * // adds class 'superClass' to element with 'containerClass'
      * adUtils.addClass('.containerClass', 'superClass')
      * @returns {void}

      hideAd
      /**
       * Hide Iframe of current creative wrapper
       * @typedef {Function} hideAd
       * @example
       * // hides this creative wrappers iframe
       * adUtils.hideAd()
       * @returns {void}
       */
    `;
  }

  return {
    moveAd,
    addClass,
    addAdvertMapIcon,
    hideAd,
    info,
    createToolTipDiv,
    createToolTipLink,
  };
}
